import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Switch,
  Row,
  Col,
  DatePicker,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { Rule, Port, TransportType, PRICING_EVENTS } from "../../types/pricing";
import { trackEvent, captureError } from "../../lib/posthog";
import dayjs from "dayjs";

const { Option } = Select;

interface BookingType {
  id: number;
  name: string;
  created_at: string;
}

interface Incoterm {
  id: number;
  name: string;
  created_at: string;
}

interface ContainerSize {
  id: number;
  name: string;
  created_at: string;
}

interface RulesTabProps {
  rules: Rule[];
  transportTypes: TransportType[];
  loading: boolean;
  fetchRules: () => Promise<void>;
}

interface RuleFormValues {
  name: string;
  description?: string;
  origin_port: string | null;
  destination_port: string | null;
  transport_type: string | null;
  shipment_type: string;
  container_size: string | null;
  incoterm: string | null;
  start_date?: dayjs.Dayjs;
  end_date?: dayjs.Dayjs;
  valid_from?: string;
  valid_to?: string | null;
  is_active?: boolean;
}

const RulesTab: React.FC<RulesTabProps> = ({
  rules,
  transportTypes,
  loading,
  fetchRules,
}) => {
  const [ruleForm] = Form.useForm<RuleFormValues>();
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [originPorts, setOriginPorts] = useState<Port[]>([]);
  const [destinationPorts, setDestinationPorts] = useState<Port[]>([]);
  const [shipmentTypes, setShipmentTypes] = useState<BookingType[]>([]);
  const [containerSizes, setContainerSizes] = useState<ContainerSize[]>([]);
  const [incoterms, setIncoterms] = useState<Incoterm[]>([]);
  const [selectedTransportType, setSelectedTransportType] =
    useState<string>("");

  // Fetch origin and destination ports separately
  const fetchPortsFromDB = async () => {
    try {
      // Get origin ports
      const { data: originPortsData, error: originError } = await supabase
        .from("booking_origin_port")
        .select("*")
        .order("name");

      if (originError) throw originError;
      setOriginPorts(originPortsData || []);

      // Get destination ports
      const { data: destPortsData, error: destError } = await supabase
        .from("booking_destination_port")
        .select("*")
        .order("name");

      if (destError) throw destError;
      setDestinationPorts(destPortsData || []);
    } catch (error) {
      console.error("Error fetching ports:", error);
      message.error("Failed to load ports");
      captureError(error, { source: "RulesTab", action: "fetchPortsFromDB" });
    }
  };

  // Fetch shipment types from the database
  const fetchShipmentTypes = async () => {
    try {
      const { data, error } = await supabase
        .from("bookings_type")
        .select("*")
        .order("name");

      if (error) throw error;
      setShipmentTypes(data || []);
    } catch (error) {
      console.error("Error fetching shipment types:", error);
      message.error("Failed to load shipment types");
      captureError(error, { source: "RulesTab", action: "fetchShipmentTypes" });
    }
  };

  // Fetch incoterms from the database
  const fetchIncoterms = async () => {
    try {
      const { data, error } = await supabase
        .from("bookings_incoterm")
        .select("*")
        .order("name");

      if (error) throw error;
      setIncoterms(data || []);
    } catch (error) {
      console.error("Error fetching incoterms:", error);
      message.error("Failed to load incoterms");
      captureError(error, { source: "RulesTab", action: "fetchIncoterms" });
    }
  };

  // Add container size fetch
  const fetchContainerSizes = async () => {
    try {
      const { data, error } = await supabase
        .from("container_size")
        .select("*")
        .order("name");

      if (error) throw error;
      setContainerSizes(data || []);
    } catch (error) {
      console.error("Error fetching container sizes:", error);
      message.error("Failed to load container sizes");
      captureError(error, {
        source: "RulesTab",
        action: "fetchContainerSizes",
      });
    }
  };

  useEffect(() => {
    fetchPortsFromDB();
    fetchShipmentTypes();
    fetchIncoterms();
    fetchContainerSizes();
  }, []);

  // Construct rule name based on form values
  const constructRuleName = (values: Partial<RuleFormValues>): string => {
    // Route part
    const originPortName =
      values.origin_port === "ALL"
        ? "All Origins"
        : originPorts.find((p) => p.id.toString() === values.origin_port)
            ?.name || "Unknown";

    const destinationPortName =
      values.destination_port === "ALL"
        ? "All Destinations"
        : destinationPorts.find(
            (p) => p.id.toString() === values.destination_port
          )?.name || "Unknown";

    const routePart =
      values.origin_port === "ALL" && values.destination_port === "ALL"
        ? "All Routes"
        : `${originPortName} to ${destinationPortName}`;

    // Transport and shipment part
    const transportTypeName =
      values.transport_type === "ALL"
        ? "All Transport Types"
        : transportTypes.find((t) => t.id.toString() === values.transport_type)
            ?.name || "Unknown";

    let transportPart = "";
    if (transportTypeName === "Airfreight") {
      transportPart = "Airfreight";
    } else {
      const shipmentTypeName =
        values.shipment_type === "ALL"
          ? "All Shipments"
          : shipmentTypes.find((t) => t.id.toString() === values.shipment_type)
              ?.name || "Unknown";

      const containerSize =
        values.container_size === "ALL"
          ? "All Sizes"
          : values.container_size
          ? containerSizes.find(
              (c) => c.id.toString() === values.container_size
            )?.name
          : "";

      if (
        transportTypeName === "All Transport Types" &&
        shipmentTypeName === "All Shipments"
      ) {
        transportPart = "All Transport Types & Shipments";
      } else if (shipmentTypeName === "All Shipments") {
        transportPart = `${transportTypeName} - All Shipments`;
      } else {
        transportPart = `${transportTypeName} - ${shipmentTypeName}${
          containerSize ? ` (${containerSize})` : ""
        }`;
      }
    }

    // Incoterm part
    const incotermName =
      values.incoterm === "ALL"
        ? "All Incoterms"
        : incoterms.find((t) => t.id.toString() === values.incoterm)?.name ||
          "Unknown";

    // Date part
    let datePart = "";
    if (values.start_date) {
      const startDate = values.start_date.format("DD-MMM-YYYY");
      const endDate = values.end_date?.format("DD-MMM-YYYY");
      datePart = endDate
        ? `Valid ${startDate} to ${endDate}`
        : `Valid from ${startDate}`;
    }

    // Combine all parts
    return [routePart, transportPart, incotermName, datePart]
      .filter(Boolean)
      .join(" | ");
  };

  // Update form values when fields change
  const handleFormValuesChange = (
    changedValues: Partial<RuleFormValues>,
    allValues: Partial<RuleFormValues>
  ) => {
    // Always update name based on current form values

    // Update selected transport type
    if (changedValues.transport_type) {
      const transportType = transportTypes.find(
        (t) => t.id.toString() === changedValues.transport_type
      );
      setSelectedTransportType(transportType?.name || "");

      // Clear shipment type if transport type is Airfreight
      if (transportType?.name === "Airfreight") {
        ruleForm.setFieldValue("shipment_type", undefined);
      }
    }

    // Route part
    const originPortName =
      allValues.origin_port === "ALL"
        ? "All Origins"
        : allValues.origin_port
        ? originPorts.find((p) => p.id.toString() === allValues.origin_port)
            ?.name || "..."
        : "...";

    const destinationPortName =
      allValues.destination_port === "ALL"
        ? "All Destinations"
        : allValues.destination_port
        ? destinationPorts.find(
            (p) => p.id.toString() === allValues.destination_port
          )?.name || "..."
        : "...";

    const routePart =
      allValues.origin_port === "ALL" && allValues.destination_port === "ALL"
        ? "All Routes"
        : `${originPortName} to ${destinationPortName}`;

    // Transport and shipment part
    const transportTypeName =
      allValues.transport_type === "ALL"
        ? "All Transport Types"
        : allValues.transport_type
        ? transportTypes.find(
            (t) => t.id.toString() === allValues.transport_type
          )?.name || "..."
        : "...";

    let transportPart = "";
    if (transportTypeName === "Airfreight") {
      transportPart = "Airfreight";
    } else {
      const shipmentTypeName =
        allValues.shipment_type === "ALL"
          ? "All Shipments"
          : allValues.shipment_type
          ? shipmentTypes.find(
              (t) => t.id.toString() === allValues.shipment_type
            )?.name || "..."
          : "...";

      const containerSize =
        allValues.container_size === "ALL"
          ? "All Sizes"
          : allValues.container_size
          ? containerSizes.find(
              (c) => c.id.toString() === allValues.container_size
            )?.name || "..."
          : "";

      if (
        transportTypeName === "All Transport Types" &&
        shipmentTypeName === "All Shipments"
      ) {
        transportPart = "All Transport Types & Shipments";
      } else if (shipmentTypeName === "All Shipments") {
        transportPart = `${transportTypeName} - All Shipments`;
      } else {
        transportPart = `${transportTypeName} - ${shipmentTypeName}${
          containerSize ? ` (${containerSize})` : ""
        }`;
      }
    }

    // Incoterm part
    const incotermName =
      allValues.incoterm === "ALL"
        ? "All Incoterms"
        : allValues.incoterm
        ? incoterms.find((t) => t.id.toString() === allValues.incoterm)?.name ||
          "..."
        : "...";

    // Date part
    let datePart = "";
    if (allValues.start_date) {
      const startDate = allValues.start_date.format("DD-MMM-YYYY");
      const endDate = allValues.end_date?.format("DD-MMM-YYYY");
      datePart = endDate
        ? `Valid ${startDate} to ${endDate}`
        : `Valid from ${startDate}`;
    }

    // Combine all parts
    const newName = [routePart, transportPart, incotermName, datePart]
      .filter(Boolean)
      .join(" | ");

    ruleForm.setFieldValue("name", newName);
  };

  const handleRuleSubmit = async (values: RuleFormValues) => {
    try {
      const formattedValues = { ...values };

      // Format the dates
      if (formattedValues.start_date) {
        formattedValues.valid_from =
          formattedValues.start_date.format("YYYY-MM-DD");
        formattedValues.valid_to =
          formattedValues.end_date?.format("YYYY-MM-DD") || null;
        delete formattedValues.start_date;
        delete formattedValues.end_date;
      }

      // Always regenerate the name based on current form values
      formattedValues.name = constructRuleName(formattedValues);

      // Convert "ALL" values to null for database
      const ruleData = {
        name: formattedValues.name,
        description: formattedValues.description,
        origin_port:
          formattedValues.origin_port === "ALL"
            ? null
            : parseInt(formattedValues.origin_port as string, 10),
        destination_port:
          formattedValues.destination_port === "ALL"
            ? null
            : parseInt(formattedValues.destination_port as string, 10),
        transport_type:
          formattedValues.transport_type === "ALL"
            ? null
            : parseInt(formattedValues.transport_type as string, 10),
        shipment_type:
          formattedValues.shipment_type === "ALL"
            ? null
            : parseInt(formattedValues.shipment_type as string, 10),
        container_size:
          formattedValues.container_size === "ALL"
            ? null
            : parseInt(formattedValues.container_size as string, 10),
        incoterm:
          formattedValues.incoterm === "ALL"
            ? null
            : parseInt(formattedValues.incoterm as string, 10),
        valid_from: formattedValues.valid_from,
        valid_to: formattedValues.valid_to,
        is_active: formattedValues.is_active ?? true,
      };

      if (editingRule) {
        // Update existing rule
        const { error } = await supabase
          .from("pricing_rules")
          .update(ruleData)
          .eq("id", editingRule.id);

        if (error) throw error;
        message.success("Rule updated successfully");

        // Track rule update
        trackEvent(PRICING_EVENTS.UPDATE_RULE, {
          rule_id: editingRule.id,
          rule_name: values.name,
        });
      } else {
        // Insert new rule - let Supabase handle ID generation
        const { error } = await supabase.from("pricing_rules").insert(ruleData);

        if (error) throw error;
        message.success("Rule added successfully");

        // Track rule creation without ID since we don't need it
        trackEvent(PRICING_EVENTS.ADD_RULE, {
          rule_name: values.name,
        });
      }

      // Refresh the rules list
      fetchRules();
      setEditingRule(null);
      ruleForm.resetFields();
    } catch (error: unknown) {
      console.error("Error saving rule:", error);
      message.error("Failed to save rule");
      captureError(error, {
        source: "RulesTab",
        action: editingRule ? "updateRule" : "createRule",
        ruleData: values,
      });
    }
  };

  const handleEditRule = (record: Rule) => {
    setEditingRule(record);

    // Reset form first to clear any previous values
    ruleForm.resetFields();

    // Set transport type first
    const transportType = record.transport_type
      ? transportTypes.find(
          (t) =>
            t.id &&
            record.transport_type?.id &&
            t.id.toString() === record.transport_type.id.toString()
        )
      : null;
    setSelectedTransportType(transportType?.name || "");

    // Format dates using dayjs
    const startDate = record.valid_from ? dayjs(record.valid_from) : undefined;
    const endDate = record.valid_to ? dayjs(record.valid_to) : undefined;

    // Handle null values by converting them to "ALL"
    const formValues: RuleFormValues = {
      name: record.name || "",
      description: record.description || "",
      origin_port: record.origin_port?.id?.toString() || "ALL",
      destination_port: record.destination_port?.id?.toString() || "ALL",
      transport_type: record.transport_type?.id?.toString() || "ALL",
      shipment_type: record.shipment_type?.id?.toString() || "ALL",
      container_size: record.container_size?.id?.toString() || "ALL",
      incoterm: record.incoterm?.id?.toString() || "ALL",
      start_date: startDate,
      end_date: endDate,
      is_active: record.is_active,
    };

    // Set all form values at once
    ruleForm.setFieldsValue(formValues);

    // Force form to update
    ruleForm.validateFields();
  };

  const handleDeleteRule = async (id: string) => {
    try {
      const { error } = await supabase
        .from("pricing_rules")
        .delete()
        .eq("id", id);

      if (error) throw error;

      message.success("Rule deleted successfully");

      // Track rule deletion
      trackEvent(PRICING_EVENTS.DELETE_RULE, {
        rule_id: id,
      });

      fetchRules();
    } catch (error: unknown) {
      console.error("Error deleting rule:", error);
      message.error("Failed to delete rule");
      captureError(error, {
        source: "RulesTab",
        action: "deleteRule",
        ruleId: id,
      });
    }
  };

  const ruleColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 300,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (description: string) => description || "-",
    },
    {
      title: "Origin Port",
      dataIndex: "origin_port",
      key: "origin_port",
      width: 150,
      render: (_: unknown, record: Rule) => (
        <span>
          {!record.origin_port ? "All Origins" : record.origin_port.name}
        </span>
      ),
    },
    {
      title: "Destination Port",
      dataIndex: "destination_port",
      key: "destination_port",
      width: 150,
      render: (_: unknown, record: Rule) => (
        <span>
          {!record.destination_port
            ? "All Destinations"
            : record.destination_port.name}
        </span>
      ),
    },
    {
      title: "Transport Type",
      dataIndex: "transport_type",
      key: "transport_type",
      width: 150,
      render: (_: unknown, record: Rule) => (
        <span>
          {!record.transport_type
            ? "All Transport Types"
            : record.transport_type.name}
        </span>
      ),
    },
    {
      title: "Shipment Type",
      dataIndex: "shipment_type",
      key: "shipment_type",
      width: 150,
      render: (_: unknown, record: Rule) => {
        if (record.transport_type?.name === "Airfreight") {
          return <span>-</span>;
        }
        return (
          <span>
            {!record.shipment_type
              ? "All Shipments"
              : record.shipment_type.name}
          </span>
        );
      },
    },
    {
      title: "Container Size",
      dataIndex: "container_size",
      key: "container_size",
      width: 150,
      render: (_: unknown, record: Rule) => {
        if (record.transport_type?.name === "Airfreight") {
          return <span>-</span>;
        }
        return (
          <span>
            {!record.container_size ? "All Sizes" : record.container_size.name}
          </span>
        );
      },
    },
    {
      title: "Incoterm",
      dataIndex: "incoterm",
      key: "incoterm",
      width: 120,
      render: (_: unknown, record: Rule) => (
        <span>{!record.incoterm ? "All Incoterms" : record.incoterm.name}</span>
      ),
    },

    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (isActive: boolean) => (
        <Switch checked={isActive} disabled size="small" />
      ),
    },
    {
      title: "Validity Period",
      dataIndex: "validity",
      key: "validity",
      width: 220,
      render: (_: unknown, record: Rule) => (
        <span>
          {record.valid_to
            ? `${dayjs(record.valid_from).format("DD-MMM-YYYY")} to ${dayjs(
                record.valid_to
              ).format("DD-MMM-YYYY")}`
            : `From ${dayjs(record.valid_from).format("DD-MMM-YYYY")}`}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right" as const,
      render: (_: unknown, record: Rule) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditRule(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteRule(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Row gutter={24}>
      <Col span={10}>
        <Card title={editingRule ? "Edit Rule" : "Add New Rule"}>
          <Form
            form={ruleForm}
            layout="vertical"
            onFinish={handleRuleSubmit}
            onValuesChange={handleFormValuesChange}
          >
            <Form.Item
              name="name"
              label="Rule Name"
              rules={[{ required: true, message: "Please enter a name" }]}
            >
              <Input
                disabled={true}
                placeholder="Name will be auto-generated from selections"
              />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item
              name="origin_port"
              label="Origin Port"
              rules={[
                {
                  required: true,
                  message: "Please select origin port",
                },
              ]}
            >
              <Select>
                <Option value="ALL">All Origins</Option>
                {originPorts.map((port) => (
                  <Option key={port.id} value={port.id.toString()}>
                    {port.name} {port.country ? `(${port.country})` : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="destination_port"
              label="Destination Port"
              rules={[
                {
                  required: true,
                  message: "Please select destination port",
                },
              ]}
            >
              <Select>
                <Option value="ALL">All Destinations</Option>
                {destinationPorts.map((port) => (
                  <Option key={port.id} value={port.id.toString()}>
                    {port.name} {port.country ? `(${port.country})` : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="transport_type"
              label="Transport Type"
              rules={[
                {
                  required: true,
                  message: "Please select transport type",
                },
              ]}
            >
              <Select>
                <Option value="ALL">All Transport Types</Option>
                {transportTypes.map((type) => (
                  <Option key={type.id} value={type.id.toString()}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedTransportType !== "Airfreight" && (
              <>
                <Form.Item
                  name="shipment_type"
                  label="Shipment Type"
                  rules={[
                    {
                      required: true,
                      message: "Please select shipment type",
                    },
                  ]}
                >
                  <Select>
                    <Option value="ALL">All Shipments</Option>
                    {shipmentTypes.map((type) => (
                      <Option key={type.id} value={type.id.toString()}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="container_size"
                  label="Container Size"
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                >
                  <Select>
                    <Option value="ALL">All Sizes</Option>
                    {containerSizes.map((size) => (
                      <Option key={size.id} value={size.id.toString()}>
                        {size.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            <Form.Item
              name="incoterm"
              label="Incoterm"
              rules={[
                {
                  required: true,
                  message: "Please select incoterm",
                },
              ]}
            >
              <Select>
                <Option value="ALL">All Incoterms</Option>
                {incoterms.map((term) => (
                  <Option key={term.id} value={term.id.toString()}>
                    {term.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Validity Period"
              required
              style={{ marginBottom: 0 }}
            >
              <Form.Item
                name="start_date"
                rules={[
                  {
                    required: true,
                    message: "Please select start date",
                  },
                ]}
                style={{ display: "inline-block", width: "calc(50% - 8px)" }}
              >
                <DatePicker
                  placeholder="Start Date"
                  style={{ width: "100%" }}
                  format="DD-MMM-YYYY"
                />
              </Form.Item>
              <Form.Item
                name="end_date"
                style={{
                  display: "inline-block",
                  width: "calc(50% - 8px)",
                  margin: "0 0 0 16px",
                }}
              >
                <DatePicker
                  placeholder="End Date (Optional)"
                  style={{ width: "100%" }}
                  format="DD-MMM-YYYY"
                />
              </Form.Item>
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Active"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingRule ? "Update" : "Add"} Rule
                </Button>
                {editingRule && (
                  <Button
                    onClick={() => {
                      setEditingRule(null);
                      ruleForm.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      <Col span={14}>
        <Card title="Pricing Rules">
          <Table
            columns={ruleColumns}
            dataSource={rules}
            rowKey="id"
            pagination={{ pageSize: 8 }}
            scroll={{ x: 1000, y: 500 }}
            loading={loading}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default RulesTab;
